'use client';

import { useTranslations } from 'next-intl';
import Confetti from 'react-confetti-boom';
import {
  PRODUCT_TOUR_CARD_WIDTH,
  ProductTourOverlay,
  useProductTour,
  type ProductTourOverlayRenderCardProps,
} from '@terra/ui-product-tour';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  boothVaultStepContent,
  boothVaultTourDefinition,
  BOOTH_VAULT_TOUR_GROUPS,
  type BoothVaultTourGroup,
} from '../../product-tours/booth-vault.tour';
import { useBoothTourHost } from '../../product-tours/useBoothTourHost';

const PHASES: BoothVaultTourGroup[] = ['discover', 'signals', 'attack'];

const PHASE_COLORS = {
  discover: '#f5a38a',
  signals: '#e65c32',
  attack: '#c44416',
} as const;

function BoothVaultTourCard({
  step,
  stepIndex,
  totalSteps,
  position,
  waitsForAction,
  showNext,
  onSkip,
  onBack,
  onNext,
}: ProductTourOverlayRenderCardProps) {
  const t = useTranslations('boothTour');
  const content = boothVaultStepContent[step.id];
  const groupId = content?.groupId ?? 'discover';
  const isLast = stepIndex === totalSteps - 1;
  const isFirst = stepIndex === 0;
  const phaseLabel = t(BOOTH_VAULT_TOUR_GROUPS[groupId].labelKey);
  const activePhaseColor = PHASE_COLORS[groupId];

  if (!content) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: PRODUCT_TOUR_CARD_WIDTH,
        pointerEvents: 'auto',
        zIndex: 1,
        transition: 'top 0.25s ease, left 0.25s ease',
        background:
          'linear-gradient(160deg, rgba(42, 26, 14, 0.97), rgba(26, 15, 6, 0.98))',
        border: '1px solid rgba(230, 92, 50, 0.35)',
        borderRadius: 16,
        padding: 20,
        color: '#faf0e6',
        boxShadow:
          '0 24px 60px rgba(0, 0, 0, 0.55), 0 0 40px rgba(230, 92, 50, 0.08)',
      }}
    >
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {PHASES.map((phase) => {
          const isCurrent = groupId === phase;
          const phaseIdx = PHASES.indexOf(phase);
          const currentPhaseIdx = PHASES.indexOf(groupId);
          const isPast = phaseIdx < currentPhaseIdx;
          return (
            <div
              key={phase}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: 3,
                  borderRadius: 2,
                  background:
                    isCurrent || isPast
                      ? PHASE_COLORS[phase]
                      : 'rgba(245, 163, 138, 0.15)',
                  transition: 'background 0.3s ease',
                }}
              />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: isCurrent
                    ? PHASE_COLORS[phase]
                    : isPast
                      ? 'rgba(245, 163, 138, 0.6)'
                      : 'rgba(245, 163, 138, 0.25)',
                  transition: 'color 0.3s ease',
                }}
              >
                {t(BOOTH_VAULT_TOUR_GROUPS[phase].labelKey)}
              </span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: activePhaseColor,
          marginBottom: 6,
        }}
      >
        {t('stepCounter', {
          phase: phaseLabel,
          current: stepIndex + 1,
          total: totalSteps,
        })}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          marginBottom: 8,
          color: '#fff',
        }}
      >
        {t(content.titleKey)}
      </div>
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: 'rgba(250, 240, 230, 0.75)',
        }}
      >
        {t(content.bodyKey)}
      </div>

      {waitsForAction ? (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#ff8a65',
          }}
        >
          {step.id === 'register' ? t('registerActionHint') : t('actionHint')}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 18,
        }}
      >
        <button
          type="button"
          onClick={onSkip}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(245, 163, 138, 0.5)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {t('skip')}
        </button>

        <div style={{ display: 'flex', gap: 8 }}>
          {!isFirst ? (
            <button
              type="button"
              onClick={onBack}
              style={{
                padding: '9px 16px',
                borderRadius: 10,
                border: '1px solid rgba(230, 92, 50, 0.25)',
                background: 'transparent',
                color: '#faf0e6',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('back')}
            </button>
          ) : null}
          {showNext ? (
            <button
              type="button"
              onClick={onNext}
              style={{
                padding: '9px 18px',
                borderRadius: 10,
                border: 'none',
                background: `linear-gradient(145deg, #ff8a65, ${activePhaseColor})`,
                color: '#1a0a03',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 12px rgba(230, 92, 50, 0.35)',
              }}
            >
              {isLast ? t('finish') : t('next')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function BoothTour() {
  const { isFeatureFlagEnabled: tourEnabled } =
    useFeatureFlags('BOOTH_TOUR_ENABLED');

  if (!tourEnabled) {
    return null;
  }

  return <BoothTourInner />;
}

function BoothTourInner() {
  const { host, conditionSnapshot } = useBoothTourHost();

  const {
    mounted,
    isOpen,
    step,
    stepIndex,
    totalSteps,
    finish,
    restart,
    goNext,
    goBack,
    actionAlreadySatisfied,
  } = useProductTour({
    definition: boothVaultTourDefinition,
    enabled: true,
    host,
    conditionSnapshot,
  });

  const showConfetti = isOpen && step?.id === 'ready-to-hack';

  return (
    <>
      {showConfetti ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 2147483000,
          }}
        >
          <Confetti x={0} y={0} spreadDeg={360} particleCount={220} />
          <Confetti x={1} y={0} spreadDeg={360} particleCount={220} />
          <Confetti x={0.5} y={0.2} spreadDeg={360} particleCount={220} />
        </div>
      ) : null}
      <ProductTourOverlay
        mounted={mounted}
        isOpen={isOpen}
        step={step}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        actionAlreadySatisfied={actionAlreadySatisfied}
        onSkip={finish}
        onBack={goBack}
        onNext={goNext}
        onRestart={restart}
        renderCard={(props) => <BoothVaultTourCard {...props} />}
      />
    </>
  );
}
